import type { CountdownApiClient } from "@countdown/api-client";
import {
  type ReactElement,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router";

import { PageHeading } from "@/components/PageHeading";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type AuditEntry,
  type AuditFilter,
  LOAD_FAILED,
  type PlatformAccess,
  ROLE_MISSING,
  loadAuditLog,
  loadPlatformAccess,
  useResource,
} from "@/lib/api";
import {
  AUDIT_EVENT_LABELS,
  AUDIT_OBJECT_LABELS,
  PLATFORM_ACCESS_KIND_LABELS,
  formatDateTime,
  platformReferenceLabel,
} from "@/lib/labels";

/** Same defaults and ceiling as the server (`services/audit.py`). */
const DEFAULT_FILTER: AuditFilter = { days: 90, limit: 50 };
const MAX_DAYS = 3650;
const MAX_LIMIT = 1000;

const INPUT_CLASS =
  "mt-1 w-full border border-input bg-background px-3 py-2 text-sm";

function byLabel(labels: Readonly<Record<string, string>>): [string, string][] {
  return Object.entries(labels).sort(([, a], [, b]) => a.localeCompare(b, "de"));
}

/** One object the page is narrowed to, chosen by a click on its name in the table. */
interface Focus {
  readonly type: string;
  readonly id: string;
  readonly label: string;
}

/**
 * The cell's audit log (concept 11.10, 12.7): approvals, emergency stops, lookups,
 * erasures, with who and why.
 *
 * The same filters as `countdown-admin audit list`, over the same server function.
 * The filters apply on "Anzeigen", not per keystroke: every change would be a request.
 * Names, actors and details come from the server (`services/audit_view.py`); a click on
 * an object shows its rows only.
 */
export function AuditPage(): ReactElement {
  const [filter, setFilter] = useState<AuditFilter>(DEFAULT_FILTER);
  const [days, setDays] = useState(String(DEFAULT_FILTER.days));
  const [limit, setLimit] = useState(String(DEFAULT_FILTER.limit));
  const [event, setEvent] = useState("");
  const [objectType, setObjectType] = useState("");
  const [focus, setFocus] = useState<Focus | null>(null);

  const load = useCallback(
    (api: CountdownApiClient) => loadAuditLog(api, filter),
    [filter],
  );
  const entries = useResource<AuditEntry[]>(load);
  const loadAccess = useCallback(
    (api: CountdownApiClient) => loadPlatformAccess(api, filter),
    [filter],
  );
  const accesses = useResource<PlatformAccess[]>(loadAccess);
  const { hash } = useLocation();
  const accessesLoaded = accesses.status === "ready";

  // The tenant menu links to `#zugriffe`; the table exists only once it loaded.
  useEffect(() => {
    if (accessesLoaded && hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    }
  }, [accessesLoaded, hash]);

  const narrow = (next: Focus | null, type: string): void => {
    setFocus(next);
    setObjectType(type);
    setFilter({
      days: Number(days),
      limit: Number(limit),
      event: event === "" ? undefined : event,
      object_type: type === "" ? undefined : type,
      object_id: next?.id,
    });
  };

  const apply = (submitted: SyntheticEvent<HTMLFormElement>): void => {
    submitted.preventDefault();
    // A focus on one object holds while its kind stays chosen.
    narrow(focus?.type === objectType ? focus : null, objectType);
  };

  if (entries.status === "forbidden") {
    return (
      <>
        <PageHeading title="Protokoll" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Protokoll" />

      <form
        className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6"
        onSubmit={apply}
      >
        <div>
          <label className="block text-sm font-medium" htmlFor="zeitraum">
            Zeitraum in Tagen
          </label>
          <input
            className={INPUT_CLASS}
            id="zeitraum"
            type="number"
            required
            min={1}
            max={MAX_DAYS}
            value={days}
            onChange={(change) => {
              setDays(change.target.value);
            }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium" htmlFor="ereignis">
            Ereignis
          </label>
          <select
            className={INPUT_CLASS}
            id="ereignis"
            value={event}
            onChange={(change) => {
              setEvent(change.target.value);
            }}
          >
            <option value="">Alle</option>
            {byLabel(AUDIT_EVENT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium" htmlFor="gegenstand">
            Gegenstand
          </label>
          <select
            className={INPUT_CLASS}
            id="gegenstand"
            value={objectType}
            onChange={(change) => {
              setObjectType(change.target.value);
            }}
          >
            <option value="">Alle</option>
            {byLabel(AUDIT_OBJECT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium" htmlFor="anzahl">
            Anzahl
          </label>
          <input
            className={INPUT_CLASS}
            id="anzahl"
            type="number"
            required
            min={1}
            max={MAX_LIMIT}
            value={limit}
            onChange={(change) => {
              setLimit(change.target.value);
            }}
          />
        </div>
        <div>
          <Button type="submit" disabled={entries.pending}>
            Anzeigen
          </Button>
        </div>
      </form>

      {focus === null ? null : (
        <p className="mb-3 text-sm">
          Nur: <strong>{focus.label}</strong>{" "}
          <button
            type="button"
            className="underline"
            onClick={() => {
              narrow(null, "");
            }}
          >
            Alle Gegenstände
          </button>
        </p>
      )}

      {entries.status === "loading" ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : null}
      {entries.status === "error" || entries.status === "not_found" ? (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      ) : null}

      {entries.status === "ready" && entries.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Keine Einträge im Zeitraum.
        </p>
      ) : null}

      {entries.status === "ready" && entries.data.length > 0 ? (
        <>
          <Table>
            <TableCaption className="sr-only">Protokolleinträge</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Ereignis</TableHead>
                <TableHead>Gegenstand</TableHead>
                <TableHead>Ausgelöst von</TableHead>
                <TableHead>Grund</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.data.map((entry, index) => (
                <TableRow key={`${entry.occurred_at}-${String(index)}`}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(entry.occurred_at)}
                  </TableCell>
                  <TableCell>
                    {AUDIT_EVENT_LABELS[entry.event_type] ?? entry.event_type}
                  </TableCell>
                  <TableCell>
                    {entry.object_id === null ||
                    focus?.id === entry.object_id ? (
                      entry.object_label
                    ) : (
                      <button
                        type="button"
                        className="text-left underline"
                        title="Nur Einträge zu diesem Gegenstand"
                        onClick={() => {
                          const id = entry.object_id;
                          if (id !== null) {
                            narrow(
                              {
                                type: entry.object_type,
                                id,
                                label: entry.object_label,
                              },
                              entry.object_type,
                            );
                          }
                        }}
                      >
                        {entry.object_label}
                      </button>
                    )}
                  </TableCell>
                  <TableCell>{entry.actor_label}</TableCell>
                  <TableCell>{entry.reason ?? ""}</TableCell>
                  <TableCell>{entry.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {entries.data.length === filter.limit ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Die Obergrenze ist erreicht: Anzahl erhöhen oder enger filtern.
            </p>
          ) : null}
        </>
      ) : null}

      <section className="mt-10" id="zugriffe">
        <h2 className="text-lg font-bold">Zugriffe des Plattformbetriebs</h2>
        {accesses.status === "loading" ? (
          <p className="mt-2 text-sm text-muted-foreground">Wird geladen …</p>
        ) : null}
        {accesses.status === "error" || accesses.status === "not_found" ? (
          <p className="mt-2 text-sm text-muted-foreground">{LOAD_FAILED}</p>
        ) : null}
        {accesses.status === "ready" && accesses.data.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Keine Zugriffe im Zeitraum.
          </p>
        ) : null}
        {accesses.status === "ready" && accesses.data.length > 0 ? (
          <Table className="mt-3">
            <TableCaption className="sr-only">
              Zugriffe des Plattformbetriebs
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Art</TableHead>
                <TableHead>Vorgang</TableHead>
                <TableHead>Begründung</TableHead>
                <TableHead>Konto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accesses.data.map((access, index) => (
                <TableRow key={`${access.occurred_at}-${String(index)}`}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(access.occurred_at)}
                  </TableCell>
                  <TableCell>
                    {PLATFORM_ACCESS_KIND_LABELS[access.access_kind]}
                  </TableCell>
                  <TableCell className="break-all">
                    {platformReferenceLabel(access.object_reference)}
                  </TableCell>
                  <TableCell>{access.reason ?? ""}</TableCell>
                  <TableCell>{access.actor_label}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </section>
    </>
  );
}
